/**
 * Public Project Service
 * Fetches projects from Firestore for the public website
 * Maps Firestore data to the existing Project interface
 */

import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Project } from '@/lib/data';
import { PROJECT_CATEGORIES, ProjectCategory } from '@/lib/types/admin';

const COLLECTION = 'projects';

// Map Firestore category to display label
const getCategoryLabel = (category: ProjectCategory): string => {
  return PROJECT_CATEGORIES[category] || category;
};

type FirestoreProjectDoc = {
  id: number | string;
  title?: string;
  slug?: string;
  category?: string;
  year?: string;
  description?: string;
  heroImage?: string;
  heroVideo?: string;
  technologies?: string[];
  liveUrl?: string;
  translations?: Project['translations'];
};

// Map Firestore project to public Project interface
const mapFirestoreToPublic = (doc: FirestoreProjectDoc): Project => {
  const data = doc;
  return {
    id: data.id, // Use Firestore document ID directly (string)
    title: data.title || '',
    slug: data.slug || '',
    category: getCategoryLabel(data.category as ProjectCategory),
    year: data.year || '',
    description: data.description || '',
    image: data.heroImage || '',
    video: data.heroVideo || undefined,
    technologies: data.technologies || [],
    liveUrl: data.liveUrl || undefined,
    translations: data.translations,
  };
};

/**
 * Get all public projects ordered by display order
 */
export const getPublicProjects = async (): Promise<Project[]> => {
  try {
    const q = query(collection(db, COLLECTION), orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => mapFirestoreToPublic({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching public projects:', error);
    return [];
  }
};

/**
 * Get featured projects for homepage
 */
export const getFeaturedPublicProjects = async (maxCount?: number): Promise<Project[]> => {
  try {
    let q = query(
      collection(db, COLLECTION),
      where('featured', '==', true),
      orderBy('order', 'asc')
    );

    if (maxCount) {
      q = query(
        collection(db, COLLECTION),
        where('featured', '==', true),
        orderBy('order', 'asc'),
        limit(maxCount)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => mapFirestoreToPublic({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching featured projects:', error);
    return [];
  }
};

/**
 * Get projects by category
 */
export const getPublicProjectsByCategory = async (category: ProjectCategory): Promise<Project[]> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('category', '==', category),
      orderBy('order', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => mapFirestoreToPublic({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching projects by category:', error);
    return [];
  }
};

/**
 * Get single project by slug
 */
export const getPublicProjectBySlug = async (slug: string): Promise<Project | null> => {
  try {
    const q = query(collection(db, COLLECTION), where('slug', '==', slug));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return mapFirestoreToPublic({ id: doc.id, ...doc.data() });
  } catch (error) {
    console.error('Error fetching project by slug:', error);
    return null;
  }
};

/**
 * Get project gallery images by slug
 */
export const getProjectGallery = async (slug: string): Promise<string[]> => {
  try {
    const q = query(collection(db, COLLECTION), where('slug', '==', slug));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return [];
    const data = snapshot.docs[0].data();
    return data.gallery || [];
  } catch (error) {
    console.error('Error fetching project gallery:', error);
    return [];
  }
};
